import { API_BASE, api } from "@/lib/apiClient";
import type { ArtikelRabattState, RabattModus } from "@/lib/artikelRabatt";
import type { ArtikelZuschlagState, ZuschlagModus } from "@/lib/artikelZuschlag";

/** Minimierte Listenfelder (ohne HERO-Sync-IDs). */
export interface ArtikelRow {
  id: number;
  hero_nr: string | null;
  hero_base_name: string | null;
  hero_base_description: string | null;
  hero_base_unit_type: string | null;
  hero_base_ean: string | null;
  hero_base_category: string | null;
  hero_base_matchcode: string | null;
  hero_base_manufacturer: string | null;
  hero_base_manufacturer_nr: string | null;
  hero_base_supplier_nr: string | null;
  hero_base_manufacturer_type_name: string | null;
  hero_base_delivery_time: string | null;
  hero_base_external_url: string | null;
  hero_base_image_src: string | null;
  hero_base_is_deleted: boolean | null;
  hero_is_deleted: boolean | null;
  deleted_at?: string | null;
  hero_supply_operator_name: string | null;
  hero_list_price?: number | null;
  hero_base_price?: number | null;
  hero_vat_percent?: number | null;
  hero_default_sales_net_price?: number | null;
  lieferant_geschaeftspartner_id?: number | null;
  lieferant_geschaeftspartner_name?: string | null;
  import_geschuetzt: boolean;
  preis_bruttopreis: number | null;
  preis_rabatt: number | null;
  preis_nettopreis: number | null;
  preis_aufschlag: number | null;
  preis_verkaufspreis: number | null;
  preis_preismenge: number | null;
  preis_einheit: string | null;
  preis_gueltig_bis: string | null;
  /** Hybride Suche: vereinfachte Treffergründe ("EAN", "Bezeichnung", …). */
  match_reasons?: string[] | null;
}

export interface ArtikelListResponse {
  items: ArtikelRow[];
  total: number;
  limit: number;
  offset: number;
  /** Nur bei Suche: Dauer der Datenbanksuche in Millisekunden. */
  search_time_ms?: number | null;
}

/** Spaltenfilter-Keys wie Backend-Query `f_*`. */
export type ArtikelColumnFilters = {
  f_hero_nr?: string;
  f_name?: string;
  f_beschreibung?: string;
  f_matchcode?: string;
  f_ean?: string;
  f_kategorie?: string;
  f_hersteller?: string;
  f_hersteller_nr?: string;
  f_lieferanten_nr?: string;
  f_einheit?: string;
  f_lieferant?: string;
  f_import_geschuetzt?: string; // "" | "true" | "false"
};

export interface ListArtikelParams {
  q?: string;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
  sort?: string;
  direction?: "asc" | "desc";
  columnFilters?: ArtikelColumnFilters;
  /** Gleiche Flags wie Materialsuche (Stundennachweise). */
  tokensAll?: boolean;
  normalize?: boolean;
  typo?: boolean;
  /** Abbruch veralteter Suchanfragen (schnelles Tippen). */
  signal?: AbortSignal;
}

function appendArtikelColumnFilters(usp: URLSearchParams, filters?: ArtikelColumnFilters) {
  if (!filters) return;
  for (const [key, value] of Object.entries(filters)) {
    const v = String(value ?? "").trim();
    if (v) usp.set(key, v);
  }
}

export async function listArtikel(params: ListArtikelParams = {}): Promise<ArtikelListResponse> {
  const usp = new URLSearchParams();
  if (params.q) usp.set("q", params.q);
  if (params.includeDeleted) usp.set("include_deleted", "true");
  if (params.limit != null) usp.set("limit", String(params.limit));
  if (params.offset != null) usp.set("offset", String(params.offset));
  if (params.sort) usp.set("sort", params.sort);
  if (params.direction) usp.set("direction", params.direction);
  appendArtikelColumnFilters(usp, params.columnFilters);
  if (params.q) {
    usp.set("tokens_all", params.tokensAll === false ? "0" : "1");
    usp.set("normalize", params.normalize === false ? "0" : "1");
    usp.set("typo", params.typo === false ? "0" : "1");
    // Semantik (Embedding-Modell) macht jede Tastatureingabe spürbar langsamer.
    usp.set("semantic", "0");
  }
  const qs = usp.toString();
  return api.get<ArtikelListResponse>(qs ? `/artikel?${qs}` : "/artikel", {
    signal: params.signal,
  });
}

export interface ArtikelPreis {
  id: number;
  bruttopreis: number | null;
  rabatt: number | null;
  nettopreis: number | null;
  aufschlag: number | null;
  verkaufspreis: number | null;
  preismenge: number | null;
  einheit: string | null;
  gueltig_bis: string | null;
  preis_quelle: string | null;
  geschuetzt: boolean;
  import_job_id: number | null;
  created_at: string | null;
}

export interface ArtikelIdentifikator {
  id: number;
  quelle: string;
  typ: string;
  wert: string;
  lieferant: string | null;
  lieferant_geschaeftspartner_id?: number | null;
  lieferant_geschaeftspartner_name?: string | null;
  created_at: string;
}

export interface ArtikelLieferantInfo {
  id: number;
  hero_contact_id?: number | null;
  firmenname: string;
  email?: string | null;
  telefon?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  hero_synced_at?: string | null;
}

export interface ArtikelHistorieAenderung {
  feld: string;
  alt: string | number | boolean | null;
  neu: string | number | boolean | null;
}

export interface ArtikelHistorieEintrag {
  id: number;
  aktion: string;
  quelle: string | null;
  import_job_id: number | null;
  geaendert_von: string | null;
  geaendert_at: string;
  aenderungen: ArtikelHistorieAenderung[] | null;
}

export interface ArtikelInternAnhang {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by: string;
  uploaded_at: string;
}

export interface ArtikelDetailCore {
  id: number;
  hero_nr: string | null;
  hero_base_name: string | null;
  hero_base_description: string | null;
  hero_base_unit_type: string | null;
  hero_base_ean: string | null;
  hero_base_category: string | null;
  hero_base_matchcode: string | null;
  hero_base_manufacturer: string | null;
  hero_base_manufacturer_nr: string | null;
  hero_base_supplier_nr: string | null;
  hero_base_manufacturer_type_name: string | null;
  hero_base_delivery_time: string | null;
  hero_base_external_url: string | null;
  hero_base_image_src: string | null;
  hero_base_is_deleted: boolean | null;
  hero_is_deleted: boolean | null;
  deleted_at?: string | null;
  hero_supply_operator_name: string | null;
  hero_list_price?: number | null;
  hero_base_price?: number | null;
  hero_vat_percent?: number | null;
  hero_default_sales_net_price?: number | null;
  import_geschuetzt: boolean;
  interne_notizen?: string | null;
  interne_notizen_updated_by?: string | null;
  interne_notizen_updated_at?: string | null;
}

export interface ArtikelDetail {
  artikel: ArtikelDetailCore;
  lieferant?: ArtikelLieferantInfo | null;
  preise: ArtikelPreis[];
  identifikatoren: ArtikelIdentifikator[];
  historie: ArtikelHistorieEintrag[];
  interne_anhaenge: ArtikelInternAnhang[];
  zuschlag?: ArtikelZuschlagState | null;
  rabatt?: ArtikelRabattState | null;
}

export async function getArtikelDetail(artikelId: number): Promise<ArtikelDetail> {
  return api.get<ArtikelDetail>(`/artikel/${artikelId}`);
}

export async function setArtikelSchutz(
  artikelId: number,
  params: { importGeschuetzt?: boolean; preisGeschuetzt?: boolean },
): Promise<void> {
  await api.patch(`/artikel/${artikelId}/schutz`, {
    import_geschuetzt: params.importGeschuetzt ?? null,
    preis_geschuetzt: params.preisGeschuetzt ?? null,
  });
}

export async function setArtikelInterneNotizen(
  artikelId: number,
  notizen: string | null,
): Promise<void> {
  await api.patch(`/artikel/${artikelId}/interne-notizen`, { notizen });
}

export async function addArtikelInternAnhang(
  artikelId: number,
  file: File,
): Promise<ArtikelInternAnhang> {
  const fd = new FormData();
  fd.append("file", file);
  return api.postForm<ArtikelInternAnhang>(`/artikel/${artikelId}/interne-anhaenge`, fd);
}

export async function deleteArtikelInternAnhang(
  artikelId: number,
  attachmentId: number,
): Promise<void> {
  await api.del(`/artikel/${artikelId}/interne-anhaenge/${attachmentId}`);
}

export function getArtikelInternAnhangUrl(artikelId: number, attachmentId: number): string {
  const base = API_BASE || "";
  return `${base}/artikel/${artikelId}/interne-anhaenge/${attachmentId}`;
}

export interface ArtikelUpdatePayload {
  hero_nr?: string;
  name?: string;
  beschreibung?: string;
  einheit?: string;
  ean?: string;
  matchcode?: string;
  hersteller?: string;
  hersteller_nr?: string;
  lieferanten_nr?: string;
  warengruppe?: string;
  lieferant_name?: string;
  lieferant_geschaeftspartner_id?: number | null;
  /** Zahl, oder null zum Leeren. Weglassen lässt den bisherigen Bruttopreis stehen. */
  preis_brutto?: number | null;
  rabatt?: number;
  preis_netto?: number;
  aufschlag?: number;
  verkaufspreis?: number;
  preismenge?: number;
  preis_einheit?: string;
  /** ISO-Datum oder null zum Leeren (unbegrenzt gültig). */
  preis_gueltig_bis?: string | null;
}

export interface ArtikelZuschlagPayload {
  modus: ZuschlagModus;
  regel_id?: number | null;
  manuell_prozent?: number | null;
  verkaufspreis?: number | null;
}

export interface ArtikelCreatePayload extends ArtikelUpdatePayload {
  /** Artikelnummer (Pflicht, muss eindeutig sein). */
  hero_nr: string;
  /** Bezeichnung (Pflicht). */
  name: string;
  zuschlag?: ArtikelZuschlagPayload;
  rabatt_auswahl?: {
    modus: RabattModus;
    regel_id?: number | null;
    manuell_prozent?: number | null;
  };
  import_geschuetzt?: boolean;
  preis_geschuetzt?: boolean;
  interne_notizen?: string | null;
}

export interface ArtikelStammdatenVorschlaege {
  matchcodes: string[];
  kategorien: string[];
}

export async function getArtikelStammdatenVorschlaege(): Promise<ArtikelStammdatenVorschlaege> {
  const data = await api.get<ArtikelStammdatenVorschlaege>("/artikel/stammdaten-vorschlaege");
  return {
    matchcodes: Array.isArray(data?.matchcodes) ? data.matchcodes.filter(Boolean) : [],
    kategorien: Array.isArray(data?.kategorien) ? data.kategorien.filter(Boolean) : [],
  };
}

export async function createArtikel(
  payload: ArtikelCreatePayload,
): Promise<{ ok: boolean; id: number }> {
  return api.post<{ ok: boolean; id: number }>("/artikel", payload);
}

export async function copyArtikel(
  artikelId: number,
): Promise<{ ok: boolean; id: number; hero_nr: string }> {
  return api.post<{ ok: boolean; id: number; hero_nr: string }>(`/artikel/${artikelId}/kopie`);
}

export async function updateArtikel(
  artikelId: number,
  payload: ArtikelUpdatePayload,
): Promise<{ ok: boolean; updated?: boolean; changes?: ArtikelHistorieAenderung[] }> {
  return api.patch(`/artikel/${artikelId}`, payload);
}

export async function softDeleteArtikel(
  artikelId: number,
): Promise<{ ok: boolean; updated: number; already_deleted?: boolean }> {
  return api.del(`/artikel/${artikelId}`);
}

export interface BulkArtikelUpdateResult {
  ok: boolean;
  updated: number;
  skipped: number;
  total: number;
  results: Array<{ id: number; ok?: boolean; skipped?: boolean; updated?: boolean; reason?: string }>;
}

export async function bulkUpdateArtikel(
  ids: number[],
  updates: ArtikelUpdatePayload,
  options?: { softDelete?: boolean },
): Promise<BulkArtikelUpdateResult> {
  return api.post<BulkArtikelUpdateResult>("/artikel/bulk-update", {
    ids,
    updates,
    soft_delete: options?.softDelete || undefined,
  });
}

export type ArtikelBulkJobStatus = "queued" | "running" | "done" | "error";

export interface ArtikelBulkJob {
  id: number;
  status: ArtikelBulkJobStatus;
  total: number;
  processed: number;
  updated: number;
  skipped: number;
  error?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export type ArtikelBulkFilterPayload = {
  q?: string;
  include_deleted?: boolean;
} & ArtikelColumnFilters;

export async function createArtikelBulkJob(body: {
  filter?: ArtikelBulkFilterPayload;
  exclude_ids?: number[];
  ids?: number[];
  updates?: ArtikelUpdatePayload;
  schutz?: { import_geschuetzt?: boolean | null; preis_geschuetzt?: boolean | null };
  zuschlag?: {
    modus: "regel" | "manuell_prozent" | "manuell_vk";
    regel_id?: number | null;
    manuell_prozent?: number | null;
    verkaufspreis?: number | null;
  };
  soft_delete?: boolean;
}): Promise<ArtikelBulkJob> {
  return api.post<ArtikelBulkJob>("/artikel/bulk-jobs", body);
}

export async function getArtikelBulkJob(jobId: number): Promise<ArtikelBulkJob> {
  return api.get<ArtikelBulkJob>(`/artikel/bulk-jobs/${jobId}`);
}
